import React, { useContext } from "react";
import { Button } from "react-bootstrap";
import { AuthContext } from "../../Context/AuthContext";
import {
  isChunkError,
  reloadOnceForChunkError,
} from "../../Utils/chunkReloadGuard";
import { captureAppError, flushErrorReports } from "../../Utils/errorReporting";
import "./RouteErrorBoundary.scss";

const WEBSITE_MASCOT_URL = "https://saleoid.com/images/errors/404-mascot.webp";

class RouteErrorBoundaryImpl extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      eventId: null,
      updating: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const eventId = captureAppError(error, {
      source: "route_error_boundary",
      errorInfo,
      userContext: this.props.userContext,
    });
    const updating = reloadOnceForChunkError(error, flushErrorReports);
    this.setState({ eventId, updating });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const needsUpdate = isChunkError(this.state.error);
    const title = needsUpdate
      ? "A newer version is ready"
      : "We hit a temporary snag";
    const description = needsUpdate
      ? "Saleoid was updated while this page was open. Refresh once to continue with the latest version."
      : "Your work is safe. Refresh the page to try again.";

    return (
      <main className="route-error" role="alert" aria-live="assertive">
        <section className="route-error__card">
          <div className="route-error__copy">
            <span className="route-error__eyebrow">
              <i className="ri-sparkling-2-line" aria-hidden="true" /> Saleoid
            </span>
            <h1>{this.state.updating ? "Updating Saleoid…" : title}</h1>
            <p>
              {this.state.updating
                ? "We are loading the latest version for you."
                : description}
            </p>
            <div className="route-error__actions">
              <Button
                className="route-error__primary"
                onClick={this.handleReload}
                type="button"
              >
                {needsUpdate ? "Refresh now" : "Reload page"}
              </Button>
            </div>
            {this.state.eventId && !needsUpdate && (
              <p className="route-error__reference">
                Support reference: <code>{this.state.eventId}</code>
              </p>
            )}
          </div>
          <div className="route-error__visual" aria-hidden="true">
            <span className="route-error__glow route-error__glow--one" />
            <span className="route-error__glow route-error__glow--two" />
            <img
              src={WEBSITE_MASCOT_URL}
              alt=""
              className="route-error__mascot"
            />
          </div>
        </section>
      </main>
    );
  }
}

const RouteErrorBoundary = ({ children }) => {
  const { userData } = useContext(AuthContext);
  const authData = userData?.authData;
  const userContext = {
    userId: authData?.id,
    teamId: authData?.currentTeam?.id || authData?.team_id,
  };

  return (
    <RouteErrorBoundaryImpl userContext={userContext}>
      {children}
    </RouteErrorBoundaryImpl>
  );
};

export default RouteErrorBoundary;
